interface DateConstructor {
    fromUTCWeek(year: number, week: number): Date;
}

interface Date {
    getUTCWeeklyYear(): number;
    setUTCWeeklyYear(value: number): number;
    getUTCWeek(): number;
    setUTCWeek(value: number): number;
    getWeeklyYear(): number;
    setWeeklyYear(value: number): number;
    getWeek(): number;
    setWeek(value: number): number;
    getUTCQuarter(): number;
    setUTCQuarter(value: number): number;
    getQuarter(): number;
    setQuarter(value: number): number;
}

interface IDateRange {
    (): IDateRange;
    periodType: string;
    first: Date;
    last: Date;
    ticks(): Date[];
    ticks(min: Date, max: Date): Date[];
}

type PropsOfType<T, P> = T extends object ? { [k in keyof T]: T[k] extends P ? k : never }[keyof T] : never;

type Orderable = boolean | string | number | Date;

interface SortedArray<T> extends Array<T> {
    thenBy(): T extends Orderable ? SortedArray<T> : never;
    thenBy(sortValueProp: PropsOfType<T, Orderable>): SortedArray<T>;
    thenBy(sortValueExtractor: (e: T) => Orderable): SortedArray<T>;
}

interface Grouping<K, T> extends Array<T> {
    key: K;
}

type FlattenedArrayElement<T> = T extends Array<infer E> ? E : T;

interface Array<T> {
    getIndexByID<TID>(id: TID): number;
    getByID<GID>(id: GID): T extends { ID: infer TID } ? TID extends GID ? T | undefined : undefined : undefined;

    copy(): T[];
    first(predicate?: (v: T) => boolean): T;
    last(predicate?: (v: T) => boolean): T;
    findLastIndex(predicate: (v: T) => boolean): number;
    count(predicate?: (v: T) => boolean): number;
    unique(): T[];
    existing(): NonNullable<T>[];
    toggle(item: T, forceInclude?: boolean): T[];
    average(): T extends number ? number : never;
    average(valueProp: PropsOfType<T, number>): number;
    average(valueExtractor: (e: T, i: number, t: T[]) => number): number;
    sum(): T extends number ? number : never;
    sum(valueProp: PropsOfType<T, number>): number;
    sum(valueExtractor: (e: T, i: number, t: T[]) => number): number;
    minBy(): T extends object ? never : T extends Orderable ? T : never;
    minBy(comparisonValueProp: PropsOfType<T, Orderable>): T;
    minBy(comparisonValueExtractor: (e: T) => Orderable): T;
    maxBy(): T extends object ? never : T extends Orderable ? T : never;
    maxBy(comparisonValueProp: PropsOfType<T, Orderable>): T;
    maxBy(comparisonValueExtractor: (e: T) => Orderable): T;
    sortBy(): T extends object ? never : T extends Orderable ? SortedArray<T> : never;
    sortBy(sortValueProp: PropsOfType<T, Orderable>): SortedArray<T>;
    sortBy(sortValueExtractor: (e: T) => Orderable): SortedArray<T>;
    groupBy(): Grouping<T, T>[];
    groupBy<K extends keyof T>(groupKeyProp: K): Grouping<T[K], T>[];
    groupBy<K>(groupKeyExtractor: (e: T, i: number, t: T[]) => K): Grouping<K, T>[];
    or<E>(valueIfEmpty: E): T[] | E;
    zip<O, R>(other: O[], zipper: (left: T, right: O, index: number, leftArray: T, rightArray: O[]) => R): R[];
    flatten<P = FlattenedArrayElement<T>>(): P[];
    intersperse<O>(item: O): T[];
    innerJoin<K, O, R>(other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T, right: O) => R): R[];
    groupJoin<K, O, R>(other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T, right: O[], leftIndex: number) => R): R[];
    leftJoin<K, O, R>(other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T, right: O | undefined, leftIndex: number, rightIndex: number | undefined) => R): R[];
    rightJoin<K, O, R>(other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T | undefined, right: O, leftIndex: number | undefined, rightIndex: number) => R): R[];
    fullJoin<K, O, R>(other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T | undefined, right: O | undefined) => R): R[];
}

interface Math {
    seededRandom(seed: number): number;
    clamp(min: number, n: number, max: number): number;
}

const DAY_MS: number = 24 * 60 * 60 * 1000;
const WEEK_MS: number = 7 * DAY_MS;

function firstDayOfFirstWeek(year: number): Date {
    const firstDayOfYear: Date = new Date(Date.UTC(year, 0, 1));
    let firstWeekOfYear: Date = firstDayOfYear;

    while (firstWeekOfYear.getUTCWeek() !== 1) {
        firstWeekOfYear = new Date(firstWeekOfYear.getTime() + DAY_MS * (firstWeekOfYear.getUTCWeek() > 5 ? 1 : -1));
    }

    const firstDayOfWeekIndex: number = 1;
    const firstDayOfWeek: Date = new Date(firstWeekOfYear.getTime() + ((firstDayOfWeekIndex - firstWeekOfYear.getUTCDay()) * DAY_MS));

    return firstDayOfWeek;
}

Date.fromUTCWeek = function (year: number, week: number): Date {
    const result: Date = firstDayOfFirstWeek(year);
    result.setUTCDate(result.getUTCDate() + (week - 1) * 7);
    return result;
};

Date.prototype.getUTCWeeklyYear = function (): number {
    return this < firstDayOfFirstWeek(this.getUTCFullYear()) ? this.getUTCFullYear() - 1 :
        this < firstDayOfFirstWeek(this.getUTCFullYear() + 1) ? this.getUTCFullYear() :
            this.getUTCFullYear() + 1;
};

Date.prototype.getUTCWeek = function (): number {
    // http://stackoverflow.com/a/32461104
    const target: Date = new Date(this.valueOf());

    const dayNumber: number = (this.getUTCDay() + 6) % 7;
    let firstThursday: number;

    target.setUTCDate(target.getUTCDate() - dayNumber + 3);
    firstThursday = target.valueOf();
    target.setUTCMonth(0, 1);

    if (target.getUTCDay() !== 4) {
        target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
    }

    return Math.ceil((firstThursday - target.valueOf()) / (WEEK_MS)) + 1;
};

Date.prototype.setUTCWeek = function (value: number): number {
    return this.setTime(+Date.fromUTCWeek(this.getUTCWeeklyYear(), value));
}

Date.prototype.getUTCQuarter = function (): number {
    return ((this.getUTCMonth() / 3) | 0) + 1;
}

Date.prototype.setUTCQuarter = function (value: number): number {
    return this.setUTCMonth((value - 1) * 3);
}

Date.prototype.getQuarter = function (): number {
    return ((this.getMonth() / 3) | 0) + 1;
}

Date.prototype.setQuarter = function (value: number): number {
    return this.setMonth((value - 1) * 3);
}

const ids = new WeakMap();

const _rebuildIndex = function (array: any[]): any | undefined {
    if (!ids.get(array) && array[0] && array[0].hasOwnProperty('ID')) {
        const map: any = {};
        ids.set(array, map);
        array.forEach((value, index) => {
            if (value && value.hasOwnProperty('ID')) {
                map[value.ID] = index;
            }
        });
    }
    return ids.get(array);
};

(Array.prototype as any).getIndexByID = function (id: string | number) {
    const map = _rebuildIndex(this);
    return map && map.hasOwnProperty(id + '') ? map[id] : -1;
};

(Array.prototype as any).getByID = function (id: string | number) {
    const map = _rebuildIndex(this);
    if (map && map.hasOwnProperty(id + '')) {
        return this[map[id]];
    }
};

(Array.prototype as any).setByID = function (id: string | number, value: any) {
    const map = _rebuildIndex(this);
    if (map && value && value.ID != id) {
        value.ID = id;
    }
    if (map && map.hasOwnProperty(id + '')) {
        this[map[id]] = value;
    }
    else if (value) {
        this.push(value);
    }
    return value;
};

(Array.prototype as any).setID = function (oldID: string | number, newID: string | number) {
    const map = _rebuildIndex(this);
    if (map && map.hasOwnProperty(oldID + '')) {
        var index = map[oldID];
        delete map[oldID];
        this[index].ID = newID;
        map[newID] = index;
    }
    return newID;
};

const push = Array.prototype.push;
Array.prototype.push = function (...values) {
    const map = ids.get(this);
    if (map) {
        for (var i = 0; i < values.length; i++) {
            if (!values[i].hasOwnProperty('ID')) {
                console.error("Can not insert value " + JSON.stringify(values[i]) + " into IDable array.");
                values.splice(i, 1);
                i--;
            } else if (map.hasOwnProperty(values[i].ID + '')) {
                console.error("Can not insert value with duplicate ID " + values[i].ID + ".");
                values.splice(i, 1);
                i--;
            }
            else {
                map[values[i].ID] = this.length + i;
            }
        }
    }

    return push.call(this, ...values);
};

const pop = Array.prototype.pop;
Array.prototype.pop = function (...args) {
    const map = ids.get(this);
    if (map && this.length) {
        delete map[this[this.length - 1].ID];
    }

    return pop.call(this, ...args);
};

const unshift = Array.prototype.unshift;
Array.prototype.unshift = function (...values) {
    const map = ids.get(this);
    if (map) {
        for (var i in map) {
            map[i] += values.length;
        }
        for (let i = 0; i < values.length; i++) {
            if (!values[i].hasOwnProperty('ID')) {
                console.error("Can not insert value " + JSON.stringify(values[i]) + " into IDable array.");
                values.splice(i, 1);
                i--;
            } else if (map.hasOwnProperty(values[i].ID + '')) {
                console.error("Can not insert value with duplicate ID " + values[i].ID + ".");
                values.splice(i, 1);
                i--;
            }
            else {
                map[values[i].ID] = i;
            }
        }
    }

    return unshift.call(this, ...values);
};

const shift = Array.prototype.shift;
Array.prototype.shift = function (...args) {
    const map = ids.get(this);
    if (map && this.length) {
        delete map[this[0].ID];
        for (var i in map) {
            map[i]--;
        }
    }

    return shift.call(this, ...args);
};

const splice = Array.prototype.splice;
Array.prototype.splice = function (start: number, count: number, ...insert: any[]) {
    const remove = splice.call(this, start, count, ...insert);
    const map = ids.get(this);
    if (map) {
        // TODO check for IDability and duplicates similar to push
        remove.filter(v => v && v.ID).forEach(v => delete map[v.ID]);
        insert.filter(v => v && v.ID).forEach(v => map[v.ID] = v);
    }
    return remove;
};

Array.prototype.unique = function () {
    return this.filter((v, i, a) => a.indexOf(v) === i);
};

Array.prototype.existing = function () {
    return this.filter(v => v !== null && v !== undefined);
};

Array.prototype.flatten = function () {
    let result = this;
    while (result.some(v => Array.isArray(v))) {
        result = result.concat.apply([], result);
    }
    return result;
};

(Array.prototype as any).group = Array.prototype.groupBy = function groupBy<T, K>(this: T[], keyPropOrExtractor?: unknown): Grouping<K, T>[] {
    const groupKeyExtractor =
        !keyPropOrExtractor ? (e: T) => e as unknown as K :
            typeof keyPropOrExtractor === 'function' ? keyPropOrExtractor as (e: T, i: number, t: T[]) => K :
                (e: T) => e[keyPropOrExtractor as unknown as keyof T] as unknown as K;

    if (!groupKeyExtractor) {
        return [[...this]] as unknown as Grouping<K, T>[];
    }

    const result: Grouping<K, T>[] = [],
        groupsByKeys = (result as any).groupsByKeys = {} as { [k: string]: any };
    for (let i = 0; i < this.length; i++) {
        const key = groupKeyExtractor(this[i], i, this) as unknown as string;
        if (!groupsByKeys[key]) {
            result.push(groupsByKeys[key] = [] as unknown as Grouping<K, T>);
            groupsByKeys[key].key = key as unknown as K;
        }
        groupsByKeys[key].push(this[i]);
    }

    return result;
};

Array.prototype.minBy = function <T>(this: T[], valuePropOrExtractor?: PropsOfType<T, Orderable> | ((e: T) => Orderable)): T {
    const comparisonValueExtractor =
        !valuePropOrExtractor ? (e: T) => e as unknown as Orderable :
            typeof valuePropOrExtractor === 'function' ? valuePropOrExtractor as ((e: T) => Orderable) :
                (e: T) => (e[valuePropOrExtractor] as unknown as Orderable);

    let result = this[0], resultValue = comparisonValueExtractor(result);
    for (let i = 1; i < this.length; i++) {
        const value = comparisonValueExtractor(this[i]);
        if (value < resultValue) {
            result = this[i];
            resultValue = value;
        }
    }

    return result;
};

Array.prototype.maxBy = function <T>(this: T[], valuePropOrExtractor?: PropsOfType<T, Orderable> | ((e: T) => Orderable)): T {
    const comparisonValueExtractor =
        !valuePropOrExtractor ? (e: T) => e as unknown as Orderable :
            typeof valuePropOrExtractor === 'function' ? valuePropOrExtractor as ((e: T) => Orderable) :
                (e: T) => (e[valuePropOrExtractor] as unknown as Orderable);

    let result = this[0], resultValue = comparisonValueExtractor(result);
    for (let i = 1; i < this.length; i++) {
        const value = comparisonValueExtractor(this[i]);
        if (value > resultValue) {
            result = this[i];
            resultValue = value;
        }
    }

    return result;
};

(Array.prototype as any).thenBy = Array.prototype.sortBy = function <T>(this: T[], valuePropOrExtractor?: PropsOfType<T, Orderable> | ((e: T) => Orderable), maybeComparator?: (a: Orderable, b: Orderable) => number): SortedArray<T> {
    const sortValueExtractor =
        !valuePropOrExtractor ? (e: T) => e as unknown as Orderable :
            typeof valuePropOrExtractor === 'function' ? valuePropOrExtractor as ((e: T) => Orderable) :
                (e: T) => (e[valuePropOrExtractor] as unknown as Orderable);
    const comparator = maybeComparator || ((a: Orderable, b: Orderable) => a < b ? -1 : a > b ? 1 : 0);

    if (!this.hasOwnProperty('_sorter')) {
        Object.defineProperty(this, '_sorter', {
            configurable: false,
            enumerable: false,
            writable: true,
            value: null
        });
    }

    const oldSorter = (this as any)._sorter;

    const sorter = (this as any)._sorter = (a: T, b: T) => oldSorter && oldSorter(a, b) || comparator!(sortValueExtractor(a), sortValueExtractor(b));

    return this.sort(sorter) as unknown as SortedArray<T>;
};

Array.prototype.intersperse = function (value) {
    if (this.length <= 1) {
        return this.concat([]);
    }

    return Array.from(Array(this.length * 2 - 1), (_, index) => index % 2 ? (typeof value === 'function' ? value(index) : value) : this[index / 2]);
};

Array.prototype.or = function <T, E>(this: T[], valueIfEmpty: E): T[] | E {
    return this.some(v => v !== undefined && v !== null) ? this : valueIfEmpty;
};

(Array.prototype as any).mean = function (this: number[]) {
    if (DEBUG && this.some(v => typeof v !== 'number')) {
        throw new Error('Can not calculate mean of non-numeric array.');
    }
    return this.length ? this.reduce((r, v) => r + v) / this.length : NaN;
};

(Array.prototype as any).meanBy = function <T>(this: T[], valueExtractor: (v: T) => number) {
    valueExtractor = valueExtractor || (a => a);
    if (DEBUG && this.some(v => typeof valueExtractor(v) !== 'number')) {
        throw new Error('Can not calculate mean of non-numeric array.');
    }
    return this.length ? this.reduce((r, v) => r + valueExtractor(v), 0) / this.length : NaN;
};

Array.prototype.average = function <T>(this: T[], valuePropOrExtractor?: PropsOfType<T, number> | ((e: T, i: number, t: T[]) => number)): number {
    return !this.length ? 0 :
        !valuePropOrExtractor ? (this as unknown as number[]).reduce((s, e) => s + e, 0) / this.length :
            typeof valuePropOrExtractor !== 'function' ? this.reduce((s, e) => s + (e[valuePropOrExtractor] as unknown as number), 0) / this.length :
                this.reduce((s, e, i, a) => s + valuePropOrExtractor(e, i, a), 0) / this.length;
};


Array.prototype.sum = function <T>(this: T[], valuePropOrExtractor?: PropsOfType<T, number> | ((e: T, i: number, t: T[]) => number)): number {
    return !this.length ? 0 :
        !valuePropOrExtractor ? (this as unknown as number[]).reduce((s, e) => s + e, 0) :
            typeof valuePropOrExtractor !== 'function' ? this.reduce((s, e) => s + (e[valuePropOrExtractor] as unknown as number), 0) :
                this.reduce((s, e, i, a) => s + valuePropOrExtractor(e, i, a), 0);
};



(Array.prototype as any).first = (Array.prototype as any).head = function <T>(this: T[], predicate?: (v: T) => boolean): T | undefined {
    return !predicate ? this[0] : this.find(predicate);
};

(Array.prototype as any).tail = function () {
    return this.slice(1);
};

(Array.prototype as any).init = function () {
    return this.slice(0, -1);
};

Array.prototype.last = function <T>(this: T[], predicate?: (v: T) => boolean): T | undefined {
    if (!predicate) {
        return this[this.length - 1];
    }
    else {
        for (let i = this.length - 1; i >= 0; i--) {
            if (predicate(this[i])) {
                return this[i];
            }
        }
        return undefined;
    }
};

Array.prototype.findLastIndex = function <T>(this: T[], predicate: (v: T) => boolean): number {
    const index = this.copy().reverse().findIndex(predicate);
    if (index === -1) return index;
    return this.length - index - 1;
}

Array.prototype.copy = Array.prototype.slice;

Array.prototype.toggle = function <T>(this: T[], element: T, shouldInclude: boolean | null = null): T[] {
    const includes = this.includes(element);
    if (shouldInclude === null) {
        shouldInclude = !includes;
    }

    if (shouldInclude !== includes) {
        shouldInclude ? this.push(element) : this.splice(this.indexOf(element), 1);
    }

    return this;
};

(Array.prototype as any).toggleAll = function (this: unknown[], elements: unknown[], shouldInclude: boolean | null = null) {
    elements.forEach(element => {
        const includes = this.includes(element);
        if (shouldInclude === null) {
            shouldInclude = !includes;
        }

        if (shouldInclude !== includes) {
            shouldInclude ? this.push(element) : this.splice(this.indexOf(element), 1);
        }
    });

    return this;
};

Array.prototype.zip = function (other, zipper) {
    return Array.from(Array(Math.min(this.length, other.length)), (_, index) => zipper(this[index], other[index], index, this, other));
};

Array.prototype.innerJoin = function <K, T, O, R>(this: T[], other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T, right: O) => R): R[] {
    const rightMap = {} as { [k: string]: O[] };
    other.forEach((rightItem, rightIndex) => {
        const rightKey = rightKeyExtractor(rightItem, rightIndex) as unknown as string;
        rightMap[rightKey] = rightMap[rightKey] || [];
        rightMap[rightKey].push(rightItem);
    });
    let result: R[] = [];
    this.forEach((leftItem, leftIndex) => {
        const rightItems = rightMap[leftKeyExtractor(leftItem, leftIndex) as unknown as string];
        if (rightItems) {
            result = result.concat(rightItems.map(rightItem => joiner(leftItem, rightItem)));
        }
    });
    return result;
};


Array.prototype.groupJoin = function <K, T, O, R>(this: T[], other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T, right: O[], leftIndex: number) => R): R[] {
    return this.map((leftItem, leftIndex) => joiner(leftItem, other.filter((rightItem, rightIndex) => leftKeyExtractor(leftItem, leftIndex) === rightKeyExtractor(rightItem, rightIndex)), leftIndex));
};

Array.prototype.leftJoin = function <K, T, O, R>(this: T[], other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T, right: O | undefined, leftIndex: number, rightIndex: number | undefined) => R): R[] {
    const rightMap = {} as { [k: string]: number };
    other.forEach((rightItem, rightIndex) => rightMap[rightKeyExtractor(rightItem, rightIndex) as unknown as string] = rightIndex);
    return this.map((leftItem, leftIndex) => joiner(leftItem, other[rightMap[leftKeyExtractor(leftItem, leftIndex) as unknown as string]], leftIndex, rightMap[leftKeyExtractor(leftItem, leftIndex) as unknown as string]));
};

Array.prototype.rightJoin = function <K, T, O, R>(this: T[], other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T | undefined, right: O, leftIndex: number | undefined, rightIndex: number) => R): R[] {
    return other.leftJoin(this, rightKeyExtractor, leftKeyExtractor, (rightItem, leftItem, rightIndex, leftIndex) => joiner(leftItem, rightItem, leftIndex, rightIndex));
};

Array.prototype.fullJoin = function <K, T, O, R>(this: T[], other: O[], leftKeyExtractor: (v: T, index: number) => K, rightKeyExtractor: (v: O, index: number) => K, joiner: (left: T | undefined, right: O | undefined) => R): R[] {
    const leftMap = {} as { [k: string]: T }, rightMap = {} as { [k: string]: O };
    this.forEach((leftItem, leftIndex) => leftMap[leftKeyExtractor(leftItem, leftIndex) as unknown as string] = leftItem);
    other.forEach((rightItem, rightIndex) => rightMap[rightKeyExtractor(rightItem, rightIndex) as unknown as string] = rightItem);
    return [
        ...this.map((leftItem, leftIndex) => joiner(leftItem, rightMap[leftKeyExtractor(leftItem, leftIndex) as unknown as string])),
        ...other.filter((rightItem, rightIndex) => !leftMap[rightKeyExtractor(rightItem, rightIndex) as unknown as string]).map(rightItem => joiner(undefined, rightItem))
    ];
};

Array.prototype.count = function (predicate) {
    return !predicate ? this.length : this.reduce((s, e) => s + (predicate(e) ? 1 : 0), 0);
};

// TODO own file?
// repeatable PRNG borrowed from http://indiegamr.com/generate-repeatable-random-numbers-in-js/
let _seed = +new Date();
Math.seededRandom = function (seed = _seed, min = 0, max = 1) {
    return min + ((_seed = (seed * 9301 + 49297) % 233280) / 233280) * (max - min);
};

Math.clamp = (min = 0, n, max = 1) => Math.max(min, Math.min(n, max));

type Writeable<T> = { -readonly [P in keyof T]: T[P] };